import { Controller } from "@hotwired/stimulus"
import L from "leaflet"
import { cornersFromPoints, planToLatLng, latLngToPlan } from "../lib/plan_geometry"
import { rotatedImage } from "../lib/rotated_image_layer"
import { planPicture } from "../lib/plan_picture"
import { track } from "../lib/mixpanel"

// Plans for Site Walks (Phelipe 2026-09-25, Site Walks list #9, #16, #18:
// "we want the ability to overlay and calibrate on the web portal as well").
// A pop-up with two steps:
//
//   1. Choose the plan - one already kept for the project, a sheet from
//      Drawings (page 1 rendered in the browser, lib/plan_picture.js), or a
//      picture/PDF of it.
//   2. Line it up (mode "map") - click a spot on the plan, then the same spot
//      on the satellite map; after two, the plan lies on the map (see-through)
//      and more spots make it fit better. Save and every walk with a GPS track
//      shows on it (site_walk_map_controller).
//
// Opened by the window event site-plan:open { mode: "map" | "pick", planId }.
// "pick" stops after step 1 and answers site-plan:picked { plan } - an indoor
// walk being placed on a floor plan (site_walk_plan_controller). A saved
// line-up answers site-plan:saved { plan }.
const IMAGERY = "https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
const PLACES = "https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
const MARK = "#2563eb"
// The divider between the plan and the map: how much of the row the plan takes, kept on this computer.
const SPLIT_KEY = "conslog.sitePlan.split"
const SPLIT_MIN = 25
const SPLIT_MAX = 75

export default class extends Controller {
  static targets = ["modal", "title", "error", "chooser", "plans", "file", "status", "liner", "step", "planMap", "groundMap",
    "opacity", "undoButton", "saveButton", "backButton", "panes", "planPane", "divider", "dividerLine",
    "sheetTools", "sheetSearch", "sheetEmpty", "sheetPane", "sheets", "sheetPreview", "noPicture",
    "typeRow", "markPick", "coords", "useCoords", "coordsError"]
  static values = { plansUrl: String, center: Array, text: Object, projectId: Number, projectPhase: String }

  connect() {
    this.plans = []
    this.sheets = []
    this._setSplit(this._savedSplit())
    this._onKey = (event) => {
      if (event.key === "Escape" && !this.modalTarget.classList.contains("hidden")) {
        event.stopImmediatePropagation()
        this.close()
      }
    }
    document.addEventListener("keydown", this._onKey, true)
  }

  disconnect() {
    document.removeEventListener("keydown", this._onKey, true)
    this._teardown()
  }

  // ── The divider between the plan and the map ─────────────────────────
  // Drag it to give one side more room (Phelipe 2026-09-25: "allow the user
  // to move the middle line"): 25–75%, remembered on this computer; a
  // double-click evens them out; arrow keys nudge it. Leaflet has to be told
  // whenever a map's box changes, or it draws the old size.

  splitDown(event) {
    if (!this.hasPanesTarget) return
    event.preventDefault()
    const box = this.panesTarget.getBoundingClientRect()
    this.dividerLineTarget.classList.add("bg-brand")
    const move = (e) => this._setSplit(((e.clientX - box.left) / box.width) * 100)
    const up = () => {
      document.removeEventListener("pointermove", move)
      document.removeEventListener("pointerup", up)
      document.removeEventListener("pointercancel", up)
      this.dividerLineTarget.classList.remove("bg-brand")
      this._sizeMaps()
    }
    document.addEventListener("pointermove", move)
    document.addEventListener("pointerup", up)
    document.addEventListener("pointercancel", up)
  }

  splitReset() {
    this._setSplit(50)
  }

  splitKey(event) {
    if (event.key === "ArrowLeft") this._setSplit(this.split - 2)
    else if (event.key === "ArrowRight") this._setSplit(this.split + 2)
    else return
    event.preventDefault()
  }

  _setSplit(pct) {
    this.split = Math.max(SPLIT_MIN, Math.min(SPLIT_MAX, Math.round(Number(pct) || 50)))
    if (this.hasPlanPaneTarget) this.planPaneTarget.style.setProperty("--split", `${this.split}%`)
    if (this.hasDividerTarget) this.dividerTarget.setAttribute("aria-valuenow", String(this.split))
    try { localStorage.setItem(SPLIT_KEY, String(this.split)) } catch { /* private mode: not remembered */ }
    this._sizeMaps()
  }

  _savedSplit() {
    try {
      const saved = Number(localStorage.getItem(SPLIT_KEY))
      return saved >= SPLIT_MIN && saved <= SPLIT_MAX ? saved : 50
    } catch {
      return 50
    }
  }

  _sizeMaps() {
    if (this._sizing) return
    this._sizing = requestAnimationFrame(() => {
      this._sizing = null
      this.planMap?.invalidateSize()
      this.groundMap?.invalidateSize()
    })
  }

  // ── Open / close ────────────────────────────────────────────────────

  async open(event) {
    const detail = event?.detail || {}
    this.mode = detail.mode === "pick" ? "pick" : "map"
    this.titleTarget.textContent = this.mode === "pick" ? this.textValue.pick_title : this.textValue.map_title
    this._error(null)
    this._status(null)
    // A drone flight's map with no GPS in it (pmfit K8-35) is lined up here
    // too: straight to the matching spots, saved to the flight.
    this.lineUp = detail.lineUp?.url && detail.lineUp.plan ? detail.lineUp : null
    this.modalTarget.classList.remove("hidden")
    if (this.lineUp?.pin) this.titleTarget.textContent = this.textValue.pin_title || this.textValue.map_title
    if (this.lineUp) {
      this._lineUp(this.lineUp.plan)
      this.backButtonTarget.hidden = true
      return
    }
    this._showStep("chooser")
    await this._load()
    const wanted = detail.planId && this.plans.find((p) => p.id === detail.planId)
    if (wanted && this.mode === "map") this._lineUp(wanted)
  }

  close() {
    if (this._making) return
    this.modalTarget.classList.add("hidden")
    this.lineUp = null
    this._teardown()
  }

  stop(event) {
    event.stopPropagation()
  }

  back() {
    this._teardown()
    this._showStep("chooser")
  }

  // ── Step 1: choose the plan ─────────────────────────────────────────

  async _load() {
    try {
      const response = await fetch(this.plansUrlValue, { headers: { Accept: "application/json" } })
      const data = await response.json()
      if (!response.ok) throw new Error(data.error || this.textValue.failed)
      this.plans = data.site_plans || []
      this.sheets = data.sheets || []
    } catch (error) {
      this._error(error.message || this.textValue.failed)
    }
    this._paintPlans()
    this._paintSheets()
  }

  _paintPlans() {
    const cards = this.plans.map((plan) => {
      const card = document.createElement("button")
      card.type = "button"
      card.className = "flex flex-col text-left rounded-lg border border-gray-200 overflow-hidden bg-white hover:border-brand cursor-pointer"
      const img = document.createElement("img")
      img.src = plan.image_url || ""
      img.alt = ""
      img.loading = "lazy"
      img.className = "w-full h-28 object-contain bg-gray-50"
      const label = document.createElement("span")
      label.className = "px-2 pt-1.5 text-[13px] font-medium text-gray-900 truncate"
      label.textContent = plan.label || this.textValue.untitled
      const state = document.createElement("span")
      state.className = `px-2 pb-1.5 text-[13px] ${plan.corners ? "text-green-700" : "text-gray-500"}`
      state.textContent = plan.corners ? this.textValue.on_map : this.textValue.not_on_map
      card.append(img, label, state)
      card.addEventListener("click", () => this._chosen(plan))
      return card
    })
    this.plansTarget.replaceChildren(...cards)
    this.plansTarget.classList.toggle("hidden", cards.length === 0)
  }

  // K6-1 (Larry, Kloepfer 2026-09-29: "I don't remember which one it is"):
  // the sheets as pictures (site plans first, from the server), a search box,
  // and a big preview of the sheet under the mouse, or of the one clicked
  // when the mouse is away. Double-click a sheet or Use this sheet takes it.
  _paintSheets() {
    if (this.hasSheetSearchTarget) this.sheetSearchTarget.value = ""
    this.searched = false
    this.picked = this.sheets[0] || null
    const none = this.sheets.length === 0
    this.sheetToolsTarget.classList.toggle("hidden", none)
    this._paintSheetGrid(this.sheets)
    if (none) this._sheetEmpty(this.textValue.no_sheets)
  }

  searchSheets() {
    const words = this.sheetSearchTarget.value.toLowerCase().split(/\s+/).filter(Boolean)
    this.searched = this.searched || words.length > 0
    const shown = this.sheets.filter((sheet) => {
      const text = `${sheet.sheet_number || ""} ${sheet.title || ""}`.toLowerCase()
      return words.every((word) => text.includes(word))
    })
    if (!shown.includes(this.picked)) this.picked = shown[0] || null
    this._paintSheetGrid(shown)
    if (shown.length === 0) this._sheetEmpty(this.textValue.no_match)
  }

  unpreviewSheet() {
    this._paintSheetPreview(this.picked)
  }

  _sheetEmpty(message) {
    this.sheetEmptyTarget.textContent = message
    this.sheetEmptyTarget.classList.remove("hidden")
    this.sheetPaneTarget.classList.add("hidden")
  }

  _paintSheetGrid(shown) {
    this.sheetEmptyTarget.classList.add("hidden")
    this.sheetPaneTarget.classList.remove("hidden")
    this.sheetCards = new Map()
    const cards = shown.map((sheet) => {
      const card = document.createElement("button")
      card.type = "button"
      card.setAttribute("role", "option")
      card.className = "flex flex-col text-left rounded-lg border border-gray-200 overflow-hidden bg-white hover:border-brand cursor-pointer"
      const picture = document.createElement("div")
      picture.className = "w-full h-28 bg-gray-50 p-1 flex items-center justify-center"
      this._sheetPicture(picture, sheet)
      const head = document.createElement("div")
      head.className = "px-2 pt-1.5 flex items-center gap-1.5 min-w-0"
      const number = document.createElement("span")
      number.className = "text-[13px] font-semibold text-gray-900 tabular-nums truncate"
      number.textContent = sheet.sheet_number || ""
      head.append(number)
      if (sheet.site_plan) head.append(this._sitePlanTag())
      const title = document.createElement("span")
      title.className = "px-2 pb-1.5 text-[13px] text-gray-600 truncate"
      title.textContent = sheet.title || ""
      card.append(picture, head, title)
      card.addEventListener("mouseenter", () => this._paintSheetPreview(sheet))
      card.addEventListener("focus", () => this._paintSheetPreview(sheet))
      card.addEventListener("click", () => this._pickSheet(sheet))
      card.addEventListener("dblclick", () => {
        this._pickSheet(sheet)
        this.useSheet()
      })
      this.sheetCards.set(sheet, card)
      return card
    })
    this.sheetsTarget.replaceChildren(...cards)
    this.sheetsTarget.scrollTop = 0
    this._markPicked()
    this._paintSheetPreview(this.picked)
  }

  _pickSheet(sheet) {
    this.picked = sheet
    this._markPicked()
    this._paintSheetPreview(sheet)
  }

  _markPicked() {
    this.sheetCards?.forEach((card, sheet) => {
      const on = sheet === this.picked
      card.classList.toggle("border-brand", on)
      card.classList.toggle("ring-1", on)
      card.classList.toggle("ring-brand", on)
      card.classList.toggle("border-gray-200", !on)
      card.setAttribute("aria-selected", on ? "true" : "false")
    })
  }

  _paintSheetPreview(sheet) {
    if (!sheet) {
      this.sheetPreviewTarget.replaceChildren()
      return
    }
    const picture = document.createElement("div")
    picture.className = "rounded-lg border border-gray-200 bg-gray-50 p-2 flex items-center justify-center"
    picture.style.aspectRatio = "3 / 2"
    this._sheetPicture(picture, sheet)
    const head = document.createElement("div")
    head.className = "mt-2 flex items-center gap-2 min-w-0"
    const number = document.createElement("span")
    number.className = "text-sm font-semibold text-gray-900 tabular-nums shrink-0"
    number.textContent = sheet.sheet_number || ""
    const title = document.createElement("span")
    title.className = "text-sm text-gray-700 truncate"
    title.textContent = sheet.title || ""
    head.append(number, title)
    if (sheet.site_plan) head.append(this._sitePlanTag())
    const parts = [picture, head]
    if (sheet.revision) {
      const rev = document.createElement("p")
      rev.className = "mt-1 text-[13px] text-gray-500"
      rev.textContent = this.textValue.rev.replace("__REV__", sheet.revision)
      parts.push(rev)
    }
    const use = document.createElement("button")
    use.type = "button"
    use.className = "mt-3 px-3 py-1.5 text-sm font-medium text-white bg-brand hover:bg-brand-dark rounded-lg transition-colors cursor-pointer whitespace-nowrap"
    use.textContent = this.textValue.use_sheet
    // The button always takes the sheet it shows, even mid-hover.
    use.addEventListener("click", () => {
      this._pickSheet(sheet)
      this.useSheet()
    })
    parts.push(use)
    this.sheetPreviewTarget.replaceChildren(...parts)
  }

  _sheetPicture(box, sheet) {
    if (sheet.thumb_url) {
      const img = document.createElement("img")
      img.src = sheet.thumb_url
      img.alt = ""
      img.loading = "lazy"
      img.className = "max-w-full max-h-full object-contain"
      img.addEventListener("error", () => box.replaceChildren(this.noPictureTarget.content.cloneNode(true)), { once: true })
      box.replaceChildren(img)
    } else {
      box.replaceChildren(this.noPictureTarget.content.cloneNode(true))
    }
  }

  _sitePlanTag() {
    const tag = document.createElement("span")
    tag.className = "inline-flex items-center shrink-0 rounded-full px-1.5 py-0.5 text-[13px] font-medium bg-green-50 text-green-700"
    tag.textContent = this.textValue.site_plan_tag
    return tag
  }

  useSheet() {
    const sheet = this.picked
    if (!sheet) {
      this._error(this.textValue.choose_sheet_first)
      return
    }
    track("Site Plan Sheet Picked", {
      project_id: this.projectIdValue, project_phase: this.projectPhaseValue, drawings_spec_id: sheet.id,
      position: this.sheets.indexOf(sheet) + 1, named_site_plan: !!sheet.site_plan, has_picture: !!sheet.thumb_url,
      searched: !!this.searched
    })
    this._make({ url: sheet.file_url }, { drawings_spec_id: sheet.id, label: [sheet.sheet_number, sheet.title].filter(Boolean).join(" — ") })
  }

  pickFile() {
    this.fileTarget.click()
  }

  fileChosen() {
    const file = this.fileTarget.files?.[0]
    this.fileTarget.value = ""
    if (!file) return
    if (!/^image\//.test(file.type) && !/\.pdf$/i.test(file.name) && file.type !== "application/pdf") {
      this._error(this.textValue.file_type)
      return
    }
    this._make({ file }, { label: file.name.replace(/\.[^.]+$/, "") })
  }

  // The plan's picture made in the browser, then the same three steps as a
  // walk: the record and a signed link, the picture straight to S3, `complete`.
  async _make(source, { drawings_spec_id = null, label = null } = {}) {
    if (this._making) return
    this._making = true
    this._error(null)
    this._status(this.textValue.making)
    try {
      const picture = await planPicture(source)
      const created = await this._send("POST", this.plansUrlValue, { drawings_spec_id, label, width: picture.width, height: picture.height })
      const put = await fetch(created.upload.url, { method: "PUT", headers: created.upload.headers || { "Content-Type": "image/jpeg" }, body: picture.blob })
      if (!put.ok) throw new Error(this.textValue.failed)
      const done = await this._send("POST", `${this.plansUrlValue}/${created.site_plan.id}/complete`, {})
      this.plans = [done.site_plan, ...this.plans]
      this._paintPlans()
      this._status(null)
      this._making = false
      this._chosen(done.site_plan)
    } catch (error) {
      this._status(null)
      this._error(error.message && error.message !== "picture" ? error.message : this.textValue.failed)
    } finally {
      this._making = false
    }
  }

  _chosen(plan) {
    if (this.mode === "pick") {
      this.dispatch("picked", { target: window, detail: { plan } })
      this.close()
    } else {
      this._lineUp(plan)
    }
  }

  // ── Step 2: line it up on the map ───────────────────────────────────

  _lineUp(plan) {
    this.plan = plan
    this.points = (plan.calibration || []).map((p) => ({ x: +p.x, y: +p.y, lat: +p.lat, lon: +p.lon }))
    this.pending = null
    this._showStep("liner")
    this._paintTypeRow()
    this.opacityTarget.value = String(plan.opacity || 0.7)
    // Leaflet measures its box: build the maps once the step is on screen.
    requestAnimationFrame(() => this._buildMaps())
  }

  _buildMaps() {
    this._teardown()
    const plan = this.plan
    this.aspect = plan.height / plan.width
    const bounds = [[0, 0], planToLatLng(1, 0, this.aspect)]
    this.planMap = L.map(this.planMapTarget, { crs: L.CRS.Simple, minZoom: -4, maxZoom: 5, zoomSnap: 0.25, attributionControl: false })
    L.imageOverlay(plan.image_url, bounds).addTo(this.planMap)
    this.planMap.fitBounds(bounds)
    this.planMap.on("click", (e) => this._planClicked(e.latlng))

    this.groundMap = L.map(this.groundMapTarget, { zoomControl: true, attributionControl: true })
    this.groundMap.attributionControl.setPrefix(false)
    L.tileLayer(IMAGERY, { maxZoom: 21, maxNativeZoom: 19, attribution: "Imagery © Esri" }).addTo(this.groundMap)
    L.tileLayer(PLACES, { maxZoom: 21, maxNativeZoom: 19, opacity: 0.85 }).addTo(this.groundMap)
    this.groundMap.createPane("sitePlan").style.zIndex = 350
    if (plan.corners) this.groundMap.fitBounds(plan.corners, { padding: [20, 20] })
    else if (this.centerValue?.length === 2) this.groundMap.setView(this.centerValue, 18)
    else this.groundMap.setView([39.5, -98.35], 4)
    this.groundMap.on("click", (e) => this._groundClicked(e.latlng))
    this._redraw()
  }

  _planClicked(latlng) {
    const [x, y] = latLngToPlan(latlng.lat, latlng.lng, this.aspect)
    if (x < 0 || x > 1 || y < 0 || y > 1) return
    if (this.pending && this.pending.lat != null) {
      this.points.push({ x, y, lat: this.pending.lat, lon: this.pending.lon })
      this.pending = null
    } else {
      this.pending = { x, y }
    }
    this._redraw()
  }

  _groundClicked(latlng) {
    if (this.pending && this.pending.x != null) {
      this.points.push({ x: this.pending.x, y: this.pending.y, lat: latlng.lat, lon: latlng.lng })
      this.pending = null
    } else {
      this.pending = { lat: latlng.lat, lon: latlng.lng }
    }
    this._redraw()
  }

  // ── Surveyed marks (Kloepfer K13-35) ────────────────────────────────
  // Only for a drone flight: the spot clicked in its picture gets the mark's
  // ground position typed (latitude, longitude) or picked from the flight's
  // uploaded survey list, instead of a click on the satellite.
  _typing() {
    return !!this.lineUp && this.hasTypeRowTarget
  }

  _paintTypeRow() {
    if (!this.hasTypeRowTarget) return
    this.typeRowTarget.classList.toggle("hidden", !this._typing())
    this._coordsError(null)
    this.coordsTarget.value = ""
    const marks = this.lineUp?.marks || []
    this.markPickTarget.classList.toggle("hidden", marks.length === 0)
    const esc = (v) => String(v ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c])
    this.markPickTarget.innerHTML = `<option value="">${esc(this.textValue.type_pick)}</option>` +
      marks.map((m, i) => `<option value="${i}">${esc(m.name)}</option>`).join("")
  }

  markPicked() {
    const mark = (this.lineUp?.marks || [])[Number(this.markPickTarget.value)]
    if (mark) this.coordsTarget.value = `${mark.lat}, ${mark.lon}`
    this._coordsError(null)
  }

  useCoords(event) {
    event?.preventDefault?.()
    if (!(this.pending?.x != null)) return this._coordsError(this.textValue.type_click_first)
    const text = this.coordsTarget.value
    const nums = (text.match(/-?\d+(?:\.\d+)?/g) || []).map(Number)
    let [lat, lon] = nums
    // "42.53591 N, 113.79283 W" as well as signed numbers.
    if (/\d\s*°?\s*S\b/i.test(text)) lat = -Math.abs(lat)
    if (/\d\s*°?\s*W\b/i.test(text)) lon = -Math.abs(lon)
    if (nums.length !== 2 || !(Math.abs(lat) <= 90) || !(Math.abs(lon) <= 180)) return this._coordsError(this.textValue.type_bad)
    this.points.push({ x: this.pending.x, y: this.pending.y, lat, lon, typed: true })
    this.pending = null
    this.coordsTarget.value = ""
    this.markPickTarget.value = ""
    this._coordsError(null)
    this.groundMap?.panTo([lat, lon])
    this._redraw()
  }

  _coordsError(message) {
    if (!this.hasCoordsErrorTarget) return
    this.coordsErrorTarget.textContent = message || ""
    this.coordsErrorTarget.classList.toggle("hidden", !message)
  }

  undo() {
    if (this.pending) this.pending = null
    else this.points.pop()
    this._redraw()
  }

  startOver() {
    this.points = []
    this.pending = null
    this._redraw()
  }

  setOpacity() {
    this.overlay?.setOpacity(Number(this.opacityTarget.value))
  }

  // Numbered marks on both maps (drag to adjust), the plan laid on the map
  // once two spots match, and what to click next.
  _redraw() {
    if (!this.planMap || !this.groundMap) return
    this.planMarks?.remove()
    this.groundMarks?.remove()
    this.planMarks = L.layerGroup().addTo(this.planMap)
    this.groundMarks = L.layerGroup().addTo(this.groundMap)
    this.points.forEach((point, i) => {
      this._mark(this.planMarks, planToLatLng(point.x, point.y, this.aspect), i + 1, (ll) => {
        const [x, y] = latLngToPlan(ll.lat, ll.lng, this.aspect)
        Object.assign(point, { x: Math.min(1, Math.max(0, x)), y: Math.min(1, Math.max(0, y)) })
      })
      this._mark(this.groundMarks, [point.lat, point.lon], i + 1, (ll) => Object.assign(point, { lat: ll.lat, lon: ll.lng }))
    })
    if (this.pending?.x != null) this._mark(this.planMarks, planToLatLng(this.pending.x, this.pending.y, this.aspect), this.points.length + 1, null, true)
    if (this.pending?.lat != null) this._mark(this.groundMarks, [this.pending.lat, this.pending.lon], this.points.length + 1, null, true)

    const corners = cornersFromPoints(this.points, this.plan.width, this.plan.height)
    if (corners) {
      if (this.overlay) this.overlay.setCorners(corners)
      else this.overlay = rotatedImage(L, this.plan.image_url, corners, { width: this.plan.width, height: this.plan.height, opacity: Number(this.opacityTarget.value), pane: "sitePlan" }).addTo(this.groundMap)
    } else if (this.overlay) {
      this.overlay.remove()
      this.overlay = null
    }

    const n = this.points.length + 1
    let step
    if (this.pending?.x != null) step = this.textValue.step_map.replace("__N__", n)
    else if (this.pending?.lat != null) step = this.textValue.step_plan_after_map.replace("__N__", n)
    else if (this.points.length === 0) step = this.textValue.step_first
    else if (this.points.length === 1) step = this.textValue.step_second
    else step = this.textValue.step_more
    if (this.pending?.x != null && this._typing()) step = (this.textValue.step_map_or_type || this.textValue.step_map).replace("__N__", n)
    this.stepTarget.textContent = step
    if (this.hasUseCoordsTarget) this.useCoordsTarget.disabled = !(this.pending?.x != null)
    this.saveButtonTarget.disabled = this.points.length < 2
    this.undoButtonTarget.disabled = this.points.length === 0 && !this.pending
  }

  // A tap on a number offers to take that spot away (Phelipe 2026-09-25:
  // "if I added too many numbers how can I remove them?"); a drag still
  // just moves it. The half-placed spot (waiting for its match) goes too.
  _offerRemove(marker, number, pending) {
    const button = L.DomUtil.create("button", "px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg cursor-pointer whitespace-nowrap")
    button.type = "button"
    button.textContent = this.textValue.remove_spot.replace("__N__", number)
    L.DomEvent.on(button, "click", (event) => {
      L.DomEvent.stop(event)
      this._removeSpot(number, pending)
    })
    marker.bindPopup(button, { closeButton: false, offset: [0, -8], autoPan: false }).openPopup()
  }

  _removeSpot(number, pending) {
    if (pending) this.pending = null
    else this.points.splice(number - 1, 1)
    this.planMap?.closePopup()
    this.groundMap?.closePopup()
    this._redraw()
  }

  _mark(group, latlng, number, onMove, pending = false) {
    const icon = L.divIcon({
      className: "",
      iconSize: [24, 24],
      iconAnchor: [12, 12],
      html: `<div style="width:24px;height:24px;border-radius:9999px;background:${pending ? "#ffffff" : MARK};color:${pending ? MARK : "#ffffff"};` +
            `border:2px solid ${pending ? MARK : "#ffffff"};display:flex;align-items:center;justify-content:center;font:600 13px sans-serif;` +
            `box-shadow:0 1px 3px rgba(0,0,0,.4)">${number}</div>`
    })
    const marker = L.marker(latlng, { icon, draggable: Boolean(onMove), keyboard: false })
    if (onMove) marker.on("dragend", () => { onMove(marker.getLatLng()); this._redraw() })
    marker.on("click", () => this._offerRemove(marker, number, pending))
    marker.addTo(group)
  }

  async save() {
    if (!this.plan || this.points.length < 2) return
    this.saveButtonTarget.disabled = true
    this._error(null)
    try {
      const url = this.lineUp ? this.lineUp.url : `${this.plansUrlValue}/${this.plan.id}`
      const data = await this._send("PATCH", url, { points: this.points, opacity: Number(this.opacityTarget.value) })
      if (data.site_plan) {
        this.plan = data.site_plan
        this.plans = [data.site_plan, ...this.plans.filter((p) => p.id !== data.site_plan.id)]
      }
      this.dispatch("saved", { target: window, detail: { plan: data.site_plan, drone_flight: data.drone_flight } })
      this._toast(this.textValue.saved)
      this.close()
    } catch (error) {
      this._error(error.message || this.textValue.failed)
      this.saveButtonTarget.disabled = false
    }
  }

  // ── Helpers ─────────────────────────────────────────────────────────

  _showStep(step) {
    this.chooserTarget.classList.toggle("hidden", step !== "chooser")
    this.linerTarget.classList.toggle("hidden", step !== "liner")
    this.saveButtonTarget.hidden = step !== "liner"
    this.backButtonTarget.hidden = step !== "liner"
  }

  _teardown() {
    this.overlay = null
    this.planMarks = null
    this.groundMarks = null
    this.planMap?.remove()
    this.groundMap?.remove()
    this.planMap = null
    this.groundMap = null
  }

  async _send(method, url, body) {
    const response = await fetch(url, {
      method,
      headers: { Accept: "application/json", "Content-Type": "application/json", "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content },
      body: JSON.stringify(body)
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok) throw new Error(data.error || this.textValue.failed)
    return data
  }

  _error(message) {
    this.errorTarget.textContent = message || ""
    this.errorTarget.classList.toggle("hidden", !message)
  }

  _status(message) {
    this.statusTarget.textContent = message || ""
    this.statusTarget.classList.toggle("hidden", !message)
  }

  _toast(message) {
    const el = document.createElement("div")
    el.className = "flash-toast-notice"
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const text = document.createElement("p")
    text.className = "flex-1 text-sm"
    text.textContent = message
    el.append(text)
    document.body.prepend(el)
  }
}
